import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2 } from 'lucide-react';

/**
 * One upload slot (proof of ID or proof of address): a file input while the
 * document is missing, the uploaded filename once it is there.
 */
const DocumentSlot = ({ inputId, question, hint, document, uploadedLabel, uploading, onUpload }) => (
  <div className="rounded-lg bg-surface px-4 py-4">
    <Label htmlFor={inputId} className="block text-[15px] font-semibold text-ink">
      {question}
    </Label>
    <p className="mt-1 text-sm text-ink-55">{hint}</p>
    {!document ? (
      <div className="mt-3 flex items-center gap-3">
        <Input
          id={inputId}
          type="file"
          accept="image/*,.pdf"
          onChange={onUpload}
          disabled={uploading}
          className="bg-ground"
        />
        {uploading && <Loader2 className="h-4 w-4 animate-spin text-ink-55" aria-label="Uploading" />}
      </div>
    ) : (
      <p className="mt-3 text-[15px] text-ink" data-testid={`${inputId}-done`}>
        Uploaded
        <span className="pd-mono ml-2 text-ink-55">{document.original_filename || uploadedLabel}</span>
      </p>
    )}
  </div>
);

/**
 * IdDocumentsCard
 * ---------------
 * Shown on a sent request until the user has uploaded at least one ID
 * document *and* let us send it automatically. State and handlers come
 * from `useIdDocuments`.
 */
const IdDocumentsCard = ({
  idDocument,
  addressDocument,
  uploadingId,
  uploadingAddress,
  idPermission,
  savingPermission,
  onDocUpload,
  onPermissionChange,
  id = 'id-documents',
}) => {
  const hasAnyDoc = !!idDocument || !!addressDocument;

  return (
    <section id={id} className="rounded-lg bg-ground px-6 py-5" data-testid="id-documents-card">
      <h2 className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink">Your ID</h2>
      <p className="mt-2 text-[15px] leading-[1.6] text-ink-70 max-w-[60ch]">
        Most organisations ask for proof of who you are before they release anything. Upload it once
        and we send it whenever one asks, encrypted so only they can open it.
      </p>

      <div className="mt-4 flex flex-col gap-3">
        <DocumentSlot
          inputId="id-upload"
          question="Which document proves who you are?"
          hint="A passport, driving licence or national ID card. A clear photo is fine."
          document={idDocument}
          uploadedLabel="ID document"
          uploading={uploadingId}
          onUpload={(e) => onDocUpload(e, 'proof_of_id')}
        />
        <DocumentSlot
          inputId="address-upload"
          question="Which document shows where you live?"
          hint="A bill, bank statement or council tax letter from the last three months."
          document={addressDocument}
          uploadedLabel="Address document"
          uploading={uploadingAddress}
          onUpload={(e) => onDocUpload(e, 'proof_of_address')}
        />
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-lg bg-surface px-4 py-4">
        <Checkbox
          id="id-permission"
          checked={idPermission}
          onCheckedChange={onPermissionChange}
          disabled={savingPermission || !hasAnyDoc}
          className="mt-0.5"
        />
        <div className="flex-1">
          <Label
            htmlFor="id-permission"
            className={`cursor-pointer text-[15px] font-semibold ${hasAnyDoc ? 'text-ink' : 'text-ink-55'}`}
          >
            Send my ID automatically when an organisation asks
          </Label>
          <p className="mt-1 text-sm text-ink-55">
            {hasAnyDoc
              ? 'You will see it in the correspondence each time, stated as a fact.'
              : 'Upload a document first, then you can turn this on.'}
          </p>
        </div>
        {savingPermission && <Loader2 className="h-4 w-4 animate-spin text-ink-55" aria-label="Saving" />}
      </div>
    </section>
  );
};

export default IdDocumentsCard;
